import { apiGetJson, apiPostJson } from "@/lib/api";
import {
  BULK_UPDATE_ACTION,
  DEACTIVATE_USER_ACTION,
  GROUP_INFO_ACTION,
} from "@/lib/apiPaths";
import { handleGlobalError } from "@/redux/reducers/globalErrorSlice";
import {
  clearLog,
  updateEditLog,
  updateFetchingGroups,
  updateGroups,
  updatePlan,
  type GroupState,
} from "@/redux/reducers/groupSlice";
import { mergeGroupInfoForStore } from "@/lib/groupEntitlement";
import {
  updateIsUserUpdateError,
  updateIsUserUpdated,
  updateAddedUsers,
} from "@/redux/reducers/userSlice";
import type { AppDispatch } from "@/redux/store";

import { fetchUserProfile } from "./userActions";

export function refreshEditLog(log: Record<string, unknown>) {
  return (dispatch: AppDispatch) => {
    dispatch(updateEditLog(log));
  };
}

export function clearEditor() {
  return (dispatch: AppDispatch) => {
    dispatch(clearLog());
  };
}

export function fetchGroups() {
  return async (dispatch: AppDispatch) => {
    dispatch(updateFetchingGroups(true));
    try {
      const data = (await apiGetJson(GROUP_INFO_ACTION)) as {
        group?: GroupState["groups"];
        plan?: Record<string, unknown>;
      };
      if (data.group) {
        dispatch(updateGroups(mergeGroupInfoForStore(data.group, data.plan) as GroupState["groups"]));
      }
      if (data.plan) dispatch(updatePlan(data.plan));
    } catch (err) {
      console.error("Error in fetchGroups:", err);
      const message = err instanceof Error ? err.message : "Unknown error";
      dispatch(
        handleGlobalError({
          title: "Unable to fetch groups",
          message: `Error fetching user groups — ${message}`,
          error: err,
        }),
      );
    } finally {
      dispatch(updateFetchingGroups(false));
    }
  };
}

export function deactivateUser(email: string) {
  return async (dispatch: AppDispatch) => {
    try {
      await apiPostJson(DEACTIVATE_USER_ACTION, { email });
      await dispatch(fetchUserProfile());
    } catch (err) {
      console.error("Error deactivating user:", err);
      const message = err instanceof Error ? err.message : "Unknown error";
      dispatch(
        handleGlobalError({
          title: "Unable to deactivate user",
          message: `Error deactivating user — ${message}`,
          error: err,
        }),
      );
      throw err;
    }
  };
}

export function modifyUsers(editLog: GroupState["editLog"]) {
  return async (dispatch: AppDispatch) => {
    try {
      const resp = await apiPostJson(BULK_UPDATE_ACTION, editLog);
      dispatch(updateIsUserUpdateError(false));
      dispatch(updateIsUserUpdated(true));
      const asArray = Array.isArray(resp) ? resp : resp != null ? [resp] : [];
      dispatch(updateAddedUsers(asArray));
      await dispatch(fetchUserProfile());
    } catch (err) {
      console.error("An error occurred while modifying user(s):", err);
      dispatch(updateIsUserUpdated(false));
      dispatch(updateIsUserUpdateError(true));
      const message = err instanceof Error ? err.message : "Unknown error";
      dispatch(
        handleGlobalError({
          title: "Unable to modify user",
          message: `An error occurred while modifying user(s) — ${message}`,
          error: err,
        }),
      );
    }
  };
}
